import { retryMultiplePaymentsThunk } from '@spike/memberships-action';
import { FunctionComponent, useEffect, useState } from 'react';
import { faCircleXmark, faEllipsis } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Grid, Typography, useMediaQuery } from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';
import { Enrollment } from '@spike/membership-model';
import {
    cancelEnrollmentThunk,
    getEnrollmentThunk,
    MembershipsStatus,
    updateEnrollmentEndAtOrPausedAtThunk
} from '@spike/memberships-action';
import useNonInitialEffect from '@versiondos/hooks';
import clsx from 'clsx';
import EnrollmentStatusChip from 'components/Memberships/Enrollment/EnrollmentStatusChip';
import { OverFullWindow, Spinner, ToolbarButton, ToolbarItem } from 'components/UI';
import { ActionsPopup } from 'components/UI/ActionsPopup/ActionsPopup';
import DefaultHeader from 'components/UI/V2/DefaultHeader/DefaultHeader';
import { useApiClientWrapper, usePermissions, useTimeZone } from 'hooks';
import useCardIcon from 'hooks/useCardIcon';
import useHideHubSpotChat from 'hooks/useHideHubSpotChat';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import {
    EnrollmentAction,
    getEnrollmentActions,
    getEnrollmentEndAtPatchValue,
    getEnrollmentResumeEndAtPatchValue
} from 'utils/EnrollmentUtils';
import CancelEnrollmentDialog from './CancelEnrollmentDialog';
import { faArrowRotateRight, faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { AlertMessage2 } from 'components/AlertMessage2';
import Button from 'components/UI/V2/Button/Button';
import useCommonStyles from './CommonStyles';
import MembershipSection from './Sections/Membership';
import MembershipItemsSection from './Sections/MembershipItems';
import ActivityLogSection from './Sections/ActivityLogSection';
import PetsSection from './Sections/Pets';
import UpdateEnrollmentCardDrawer from './UpdateEnrollmentCardDrawer';
import AdjustUsageDrawer from './AdjustUsageDrawer';
import useEnrollmentCheckout from 'hooks/useEnrollmentCheckout';
import EnrollmentCheckoutWrapper from './EnrollmentCheckoutWrapper';
import ResumeEnrollmentDialog from './ResumeEnrollmentDialog';

interface EnrollmentDetailsProps {
    enrollmentId: number;
    onClose: () => void;
}

const EnrollmentDetails: FunctionComponent<EnrollmentDetailsProps> = ({ enrollmentId, onClose }) => {
    const classes = useCommonStyles();

    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const permissions = usePermissions();
    const timezone = useTimeZone();
    const enrollment = useSelector<RootState, Enrollment | null>(state => state.memberships.enrollmentSelected);
    const membershipsStatus = useSelector<RootState, MembershipsStatus>(state => state.memberships.status);
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
    const [showMobileDropdown, setShowMobileDropdown] = useState(false);
    const [actionType, setActionType] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadingCards, setLoadingCards] = useState(false);
    const [showCancelConfirmDialog, setShowCancelConfirmDialog] = useState(false);
    const [showResumeConfirmDialog, setShowResumeConfirmDialog] = useState(false);
    const [isUpdatingPauseResume, setIsUpdatingPauseResume] = useState(false);
    const [showClientCards, setShowClientCards] = useState(false);
    const [showAdjustUsage, setShowAdjustUsage] = useState(false);
    const [isRetryingPayment, setIsRetryingPayment] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const { checkoutInfo, openCheckout, closeCheckout, handlePaymentSuccess } = useEnrollmentCheckout();

    const cardIcon = useCardIcon(enrollment?.paymentMethod?.name || '');
    useHideHubSpotChat(showMobileDropdown);

    const getEnrollment = () => {
        dispatch(getEnrollmentThunk(apiClientWrapper, enrollmentId));
    };

    useEffect(() => {
        setRetryCount(0);
        getEnrollment();
    }, [enrollmentId]);

    useNonInitialEffect(() => {
        switch (membershipsStatus) {
            case MembershipsStatus.GettingEnrollment:
                setLoading(true);
                break;
            case MembershipsStatus.GetEnrollmentSuccess:
                setLoading(false);
                setLoadingCards(false);
                break;
            case MembershipsStatus.GetEnrollmentError:
                setError('Error loading enrollment');
                setLoading(false);
                break;
            case MembershipsStatus.CancelEnrollmentSuccess:
                getEnrollment();
                break;
            case MembershipsStatus.UpdateEndDateSuccess:
            case MembershipsStatus.UpdateEndDateError:
                if (isUpdatingPauseResume) {
                    setIsUpdatingPauseResume(false);
                    setShowResumeConfirmDialog(false);
                    getEnrollment();
                }
                break;
            case MembershipsStatus.RetryingPayment:
                setIsRetryingPayment(true);
                break;
            case MembershipsStatus.RetryPaymentSuccess:
                setShowClientCards(false);
                setIsRetryingPayment(false);
                setLoading(true);
                break;
            case MembershipsStatus.RetryPaymentError:
                setIsRetryingPayment(false);
                break;
            case MembershipsStatus.RetryMultiplePaymentsError:
                setIsRetryingPayment(false);
                break;
            case MembershipsStatus.RetryMultiplePaymentsSuccess:
                setShowClientCards(false);
                setIsRetryingPayment(false);
                setLoading(true);
                break;
            case MembershipsStatus.RetryingMultiplePayments:
                setIsRetryingPayment(true);
                break;
        }
    }, [isUpdatingPauseResume, membershipsStatus]);

    if (error) {
        return (
            <OverFullWindow
                showMobileLogo
                title={''}
                onClose={() => onClose()}
            >
                <Box className={clsx(classes.loadingContainerError, classes.loadingContainer)}>
                    <img
                        className={classes.imageEmpty}
                        src="/images/memberships/clients-notfound.svg"
                    />
                    <Typography variant="h6">Error loading membership details</Typography>
                </Box>
            </OverFullWindow>
        );
    }

    if (!enrollment) {
        return (
            <OverFullWindow
                showMobileLogo
                title={''}
                onClose={() => onClose()}
            >
                <Box className={classes.loadingContainer}>
                    <Spinner />
                </Box>
            </OverFullWindow>
        );
    }

    // TODO: Remove this after testing
    //enrollment.status = 'past_due';

    const handleEnrollmentAction = (action: EnrollmentAction) => {
        setActionType(action);

        switch (action) {
            case 'update_card':
                setShowClientCards(true);
                break;
            case 'try_again':
                setRetryCount(prev => prev + 1);
                handleRetryPayment(enrollmentId);
                break;
            case 'pause':
                handlePauseEnrollment();
                break;
            case 'cancel':
                setShowCancelConfirmDialog(true);
                break;
            case 'resume':
                setShowResumeConfirmDialog(true);
                break;
            case 'collect_payment':
                openCheckout(enrollment);
                break;
            default:
                break;
        }
    };

    const membershipTitle = () => {
        return (
            <div className={classes.enrollmentTitle}>
                {enrollment.membership.name}{' '}
                <EnrollmentStatusChip
                    className={classes.badge}
                    status={enrollment.status}
                />
            </div>
        );
    };

    const titleTooltipOptions = () => {
        if (enrollment.status === 'canceled') {
            return <></>;
        }

        return isMobile ? (
            <button
                type="button"
                aria-label="Membership Details"
                className={classes.membershipOptionsButton}
                onClick={() => setShowMobileDropdown(true)}
            >
                <FontAwesomeIcon icon={faEllipsis} />
            </button>
        ) : (
            <ToolbarButton
                id="memberships_toolbar"
                className={classes.toolbarButton}
            >
                {getEnrollmentActions(enrollment.status, enrollment.membership.enrollmentType, permissions).map(
                    (action, index) => (
                        <ToolbarItem
                            key={index}
                            text={action.label}
                            icon={action.icon}
                            onClick={() => handleEnrollmentAction(action.action)}
                        />
                    )
                )}
            </ToolbarButton>
        );
    };

    const handleConfirmCancel = (reason: string) => {
        if (!enrollment) return;
        const canceledAt = new Date().toISOString();
        dispatch(cancelEnrollmentThunk(apiClientWrapper, enrollment.id, canceledAt, reason));
        setShowCancelConfirmDialog(false);
    };

    const handlePauseEnrollment = () => {
        if (
            !enrollment ||
            !permissions.memberships.pauseResume ||
            isUpdatingPauseResume ||
            enrollment.membership.enrollmentType !== 'package'
        ) {
            return;
        }

        setIsUpdatingPauseResume(true);

        dispatch(
            updateEnrollmentEndAtOrPausedAtThunk(
                apiClientWrapper,
                enrollment.id,
                getEnrollmentEndAtPatchValue(enrollment, timezone),
                new Date().toISOString()
            )
        );
    };

    const handleResumeEnrollment = (extendEndDate: boolean) => {
        if (
            !enrollment ||
            !permissions.memberships.pauseResume ||
            isUpdatingPauseResume ||
            enrollment.membership.enrollmentType !== 'package'
        ) {
            return;
        }

        setIsUpdatingPauseResume(true);

        dispatch(
            updateEnrollmentEndAtOrPausedAtThunk(
                apiClientWrapper,
                enrollment.id,
                extendEndDate
                    ? getEnrollmentResumeEndAtPatchValue(enrollment, timezone)
                    : getEnrollmentEndAtPatchValue(enrollment, timezone),
                null
            )
        );
    };

    const refreshEnrollment = () => {
        setTimeout(() => {
            getEnrollment();
        }, 3000);
    };

    const getPaymentAttempts = () => {
        return enrollment.pastDueEnrollments ? enrollment.pastDueEnrollments.length + 1 : 1;
    };

    const totalAmountDue = () => {
        return (Number(enrollment.totalAmount) * getPaymentAttempts()).toFixed(2);
    };

    const getAlertMessage = () => {
        if (enrollment.status === 'canceled') {
            return (
                <AlertMessage2
                    icon={faCircleXmark}
                    iconColor="#BAA997"
                    bgColor="#F8F5F1"
                    bdColor="#BAA997"
                    className={classes.alertMessage}
                >
                    <Typography>
                        <b>Cancellation reason:</b> {enrollment.cancellationReason || 'Client canceled'}
                    </Typography>
                </AlertMessage2>
            );
        }

        if (enrollment.status === 'pending' && enrollment.paymentLink === true) {
            return (
                <AlertMessage2
                    icon={faExclamationTriangle}
                    iconColor="#EAB464"
                    bgColor="#F8F5F1"
                    bdColor="#BCB8AE"
                    className={classes.alertMessage}
                >
                    <Typography>
                        Payment is currently in progress via Pay by Link. Wait for the client to complete the
                        transaction or collect payment here.
                    </Typography>
                    <Box className={classes.buttonContainer}>
                        <Button
                            size="medium"
                            variant="black"
                            label="Collect Payment"
                            onClick={() => openCheckout(enrollment)}
                        />
                    </Box>
                </AlertMessage2>
            );
        }

        if (enrollment.status === 'past_due') {
            return (
                <AlertMessage2
                    icon={faExclamationTriangle}
                    iconColor="#EF4F57"
                    bgColor="rgba(251, 185, 188, 0.20)"
                    bdColor="#EF4F57"
                    bWidth={'2px'}
                    className={classes.alertMessage}
                >
                    <Typography>
                        We couldnt charge the card{' '}
                        {enrollment.paymentMethod?.lastDigits
                            ? 'ending in ' + enrollment.paymentMethod?.lastDigits + ' '
                            : ''}
                        . This <b>was attempt #{getPaymentAttempts()}</b>. The total amount due is{' '}
                        <b>${totalAmountDue()}</b>. Please update the payment method to avoid interruption.
                    </Typography>
                    {permissions.memberships.managePayments && (
                        <Box className={classes.buttonContainer}>
                            <Button
                                size="medium"
                                variant="black"
                                label="Update Card"
                                onClick={() => handleEnrollmentAction('update_card')}
                            />
                            {retryCount < 3 && (
                                <Typography
                                    className={classes.optionAction}
                                    onClick={() => !isRetryingPayment && handleEnrollmentAction('try_again')}
                                    style={{
                                        pointerEvents: isRetryingPayment ? 'none' : 'auto',
                                        opacity: isRetryingPayment ? 0.6 : 1,
                                        cursor: isRetryingPayment ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {isRetryingPayment ? (
                                        <>
                                            <Spinner
                                                className={classes.spinnerRetry}
                                                classNameCircularProgress={classes.spinnerCircularProgressRetry}
                                            />{' '}
                                            Try Again
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faArrowRotateRight} /> Try Again
                                        </>
                                    )}
                                </Typography>
                            )}
                        </Box>
                    )}
                </AlertMessage2>
            );
        }

        return null;
    };

    const handleCloseClientCards = () => {
        setShowClientCards(false);
    };

    const getEnrollmentIds = () => {
        return enrollment.pastDueEnrollments
            ? enrollment.pastDueEnrollments.map(e => e.id).join(',')
            : enrollment.id.toString();
    };

    const handleRetryPayment = async (enrollmentId: number) => {
        let enrollmentIds = getEnrollmentIds();
        if (enrollment.pastDueEnrollments && enrollment.pastDueEnrollments.length > 0) {
            enrollmentIds = enrollmentIds + ',' + enrollment.id;
        }
        //await dispatch(retryPaymentThunk(apiClientWrapper, enrollmentId));
        await dispatch(retryMultiplePaymentsThunk(apiClientWrapper, enrollmentIds));
        refreshEnrollment();
    };

    const onFinishUpdate = () => {
        setLoadingCards(true);

        if (enrollment.status == 'past_due') {
            refreshEnrollment();
        } else {
            getEnrollment();
        }
    };

    const syncLoading = () => {
        return loading || loadingCards;
    };

    const clientCards = () => {
        return (
            <UpdateEnrollmentCardDrawer
                onClose={handleCloseClientCards}
                enrollment={enrollment}
                onFinishUpdate={onFinishUpdate}
                loading={syncLoading()}
            />
        );
    };

    const renderEnrollmentContent = () => {
        if (loading || !enrollment) {
            return (
                <Box className={classes.loadingContainer}>
                    <Spinner />
                </Box>
            );
        }

        return (
            <>
                {/* Header Section */}
                <Box className={classes.headWrapper}>
                    <DefaultHeader
                        title={membershipTitle()}
                        displayButton={false}
                        id="memberships_sell_button"
                        className={classes.headerContainer}
                        tooltipOptions={titleTooltipOptions()}
                        textBelowTitle={'ID: ' + enrollment.id}
                    />
                </Box>

                {getAlertMessage() && <Box className={classes.boxAlerts}>{getAlertMessage()}</Box>}

                {/* Main Content Section */}
                <Box className={classes.wrapperContainer}>
                    <Grid
                        container
                        spacing={3}
                    >
                        <Grid
                            item
                            xs={12}
                            md={3}
                        >
                            <PetsSection
                                pets={enrollment.pets}
                                classes={classes}
                            />
                        </Grid>

                        <Grid
                            item
                            xs={12}
                            md={9}
                        >
                            <MembershipSection
                                enrollment={enrollment}
                                classes={classes}
                                cardIcon={cardIcon}
                                onSaved={getEnrollment}
                            />
                            <MembershipItemsSection
                                items={enrollment.membership.membershipItems}
                                classes={classes}
                                isPackage={enrollment.membership.enrollmentType === 'package'}
                                onAdjustUsage={
                                    permissions.memberships.adjustUsage ? () => setShowAdjustUsage(true) : undefined
                                }
                            />
                            <ActivityLogSection
                                enrollment={enrollment}
                                classes={classes}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </>
        );
    };

    return (
        <>
            <OverFullWindow
                showMobileLogo
                title={enrollment.clientName ?? ''}
                onClose={() => onClose()}
                detailContainerClassName={classes.detailContainer}
                className={classes.detailMainContainer}
            >
                <Box className={classes.membershipDetailsBody}>
                    <Box className={classes.membershipDetailsContainer}>{renderEnrollmentContent()}</Box>
                </Box>
            </OverFullWindow>

            {showMobileDropdown && isMobile && (
                <ActionsPopup
                    actions={getEnrollmentActions(
                        enrollment.status,
                        enrollment.membership.enrollmentType,
                        permissions
                    ).map(action => ({
                        ...action,
                        onClick: () => {
                            setShowMobileDropdown(false);
                            handleEnrollmentAction(action.action);
                        }
                    }))}
                    onClose={() => setShowMobileDropdown(false)}
                />
            )}

            <CancelEnrollmentDialog
                open={showCancelConfirmDialog}
                enrollment={enrollment}
                onCancel={() => setShowCancelConfirmDialog(false)}
                onConfirm={handleConfirmCancel}
                questionClassName={classes.confirmQuestion}
                subquestionClassName={classes.select}
            />

            <ResumeEnrollmentDialog
                open={showResumeConfirmDialog}
                processing={isUpdatingPauseResume}
                onCancel={() => setShowResumeConfirmDialog(false)}
                onResume={handleResumeEnrollment}
            />

            {showClientCards && clientCards()}

            {showAdjustUsage && enrollment.membership.enrollmentType === 'package' && (
                <AdjustUsageDrawer
                    enrollment={enrollment}
                    onClose={() => setShowAdjustUsage(false)}
                    onSaved={() => {
                        setShowAdjustUsage(false);
                        getEnrollment();
                    }}
                />
            )}

            <EnrollmentCheckoutWrapper
                checkoutInfo={checkoutInfo}
                onClose={closeCheckout}
                onPaid={() => {
                    closeCheckout();
                    refreshEnrollment();
                }}
            />
        </>
    );
};

export default EnrollmentDetails;
